<template>
  <div class="relative min-h-[400px]">
    <site-loader v-if="loading" :top="true" />
    <div v-show="step == 1">
      <div class="mb-5 flex">
        <div
          v-for="(label, value) in genders"
          :key="value"
          :class="
            'me-3.5 block cursor-pointer rounded-md border px-5 py-1.5 text-center text-sm font-medium' +
            (errors.includes('gender')
              ? ' border-red-500'
              : person.gender == value
                ? ' border-red-500 bg-white'
                : ' border-white bg-dark-20')
          "
          @click="updatePerson('gender', value)"
        >
          <span class="tiny-screen-hidden">{{ label }}</span>
          <span class="tiny-screen-visible">{{ label.charAt(0) }}</span>
        </div>
      </div>

      <div class="mb-3.5 grid grid-cols-1 gap-3.5 md:mb-5 md:grid-cols-4 md:gap-5">
        <form-select
          :label="t('Postcode / City')"
          :options="postcodes"
          :error="errors.includes('postcode')"
          :value="person.postcode?.value || 0"
          :initial-value="postcodeLoaded ? undefined : initialPostcode"
          :has-search="true"
          wrapper-classes="md:col-span-2"
          :no-matches-label="t('No postcodes found')"
          @change="updatePerson('postcode', $event)"
          @search="searchPostcode"
        />
        <form-input
          :label="t('Date of Birth')"
          :value="person.dob"
          type="text"
          :datepicker="true"
          :datepicker-options="datepickerOptions"
          @change="updatePerson('dob', $event.target.value)"
        />
        <form-select
          :label="t('Deductible')"
          :options="deductibles"
          :error="errors.includes('deductible')"
          :value="person.deductible?.value || 0"
          @change="updatePerson('deductible', $event)"
        />
      </div>
      <div v-if="priceLoaded" ref="pricesContainer">
        <label class="mb-5 block font-medium text-dark-1">{{ t("General Plans") }}</label>
        <div v-if="prices.length == 0" class="mb-5 text-sm">
          {{ t("No plans found for your criterias") }}
        </div>
        <div v-if="prices.length > 0" class="mb-5 grid grid-cols-1 gap-3.5 md:grid-cols-2 md:gap-5">
          <div
            v-for="price in prices"
            :key="price.id"
            :class="
              'block cursor-pointer rounded-md border bg-dark-20 p-2.5 font-medium ' +
              (person.price && person.price.id == price.id ? 'border-red-500 bg-white' : 'border-white')
            "
            @click="updatePerson('price', price)"
          >
            <div class="flex justify-between">
              <div class="relative mb-1.5 flex grow items-center">
                <p class="heading mb-0 text-sm text-dark">
                  {{ price.plan.heading }}
                </p>
                <tool-tip :html="price.plan.tooltip" />
              </div>
              <p class="price mb-0 text-sm font-semibold text-dark">
                {{ formatCurrency(price.value) }}
              </p>
            </div>
            <p class="mb-0 text-2xs text-dark-28">
              {{ price.plan.subheading }}
            </p>
          </div>
        </div>
        <div v-if="person.price && person.price.accident">
          <div class="relative mb-5 flex items-center">
            <label class="font-medium text-dark-1">{{ t("Accident Cover") }}</label>
            <tool-tip :html="accidentCoverTooltip" />
          </div>
          <div class="mb-5 grid grid-cols-1 gap-3.5 md:grid-cols-2 md:gap-5">
            <div
              :class="
                'rounded-md border bg-dark-20 p-2.5 font-medium' +
                (person.accidentCover ? ' border-red-500 bg-white' : ' border-white')
              "
            >
              <div class="flex flex-col">
                <div class="mb-1.5 flex justify-between">
                  <span class="label text-sm font-medium text-dark">
                    {{ t("Include Accident Cover") }}
                  </span>
                  <span class="price text-sm font-semibold text-dark">{{
                    formatCurrency(person.price.accident.price)
                  }}</span>
                </div>
                <div class="flex items-start justify-between">
                  <span class="subheading text-2xs text-dark-28">
                    {{ person.price.plan.heading }}
                  </span>
                  <form-switch
                    :checked="person.accidentCover"
                    @change="updatePerson('accidentCover', $event.target.checked)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="optionalsLoaded">
          <label class="mb-5 block font-medium text-dark-1">{{ t("Additional features") }}</label>
          <div v-if="optionals.length == 0" class="text-sm">
            {{ t("No additional products found, have you chosen your gender?") }}
          </div>
          <div v-for="typeId in Object.keys(optionals)" :key="typeId" class="mb-5">
            <div class="relative mb-5 flex items-center">
              <label class="title block text-xs font-medium text-dark-1">{{ optionals[typeId].title }}</label>
              <tool-tip :html="optionals[typeId].tooltip" />
            </div>
            <div class="grid grid-cols-1 gap-3.5 md:grid-cols-2 md:gap-5">
              <calculator-additional
                v-for="(additional, index) in optionals[typeId].additionals"
                :key="index"
                :additional="additional"
                :selected="(person.optionals || []).map((a) => a.id).includes(additional.id)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-show="step == 2">
      <div class="mb-5 grid grid-cols-1 gap-3.5 md:grid-cols-2 md:gap-5">
        <form-input
          :label="t('First Name')"
          :value="person.firstName"
          type="text"
          :error="errors.includes('firstName')"
          @change="updatePerson('firstName', $event.target.value)"
        />
        <form-input
          :label="t('Last Name')"
          :value="person.lastName"
          type="text"
          :error="errors.includes('lastName')"
          @change="updatePerson('lastName', $event.target.value)"
        />
        <form-input
          :label="t('Email')"
          :value="person.email"
          type="email"
          :error="errors.includes('email')"
          @change="updatePerson('email', $event.target.value)"
        />
        <form-select
          :label="t('Current Insurance Provider')"
          :value="person.insuranceProvider?.value || 0"
          :options="insuranceProviders"
          @change="updatePerson('insuranceProvider', $event)"
        />
      </div>
      <div class="mb-5 flex flex-col justify-between sm:flex-row sm:items-center">
        <div class="relative flex grow items-center">
          <span class="text-xl text-dark-2">{{ t("Select your current doctor") }}</span>
          <tool-tip :html="doctorTooltip" />
        </div>
        <a href="#" class="text-base text-red-500" @click.prevent="showProviders = !showProviders">{{
          showProviders ? t("Choose later") : t("Choose my doctor")
        }}</a>
      </div>
      <div v-if="showProviders" class="rich-text small-p mb-5" v-html="selectDoctorText"></div>
      <div v-if="showProviders" class="flex flex-col rounded-lg bg-dark-29 p-3.5">
        <div class="mb-3.5 flex rounded-md bg-white p-1.5">
          <span class="icon search-loupe me-2.5 size-6"></span>
          <input
            type="text"
            class="w-full bg-white py-[3px] text-sm leading-3"
            :placeholder="t('Search')"
            @keydown="searchProviders"
          />
        </div>
        <div class="relative max-h-[400px]">
          <div v-if="loadingProviders" class="h-[100px]">
            <site-loader />
          </div>

          <div v-if="!loadingProviders" class="grid grid-cols-1 overflow-y-auto">
            <div
              v-for="provider in providers"
              :key="provider.id"
              class="mx-2.5 flex items-start justify-between border-b border-dark-33 py-2.5"
            >
              <div class="flex items-start">
                <img :src="provider.image" class="me-2.5 size-10 rounded-full" />
                <label class="me-2.5 flex cursor-pointer flex-col" :for="'select-provider-' + provider.id">
                  <span class="text-base font-medium">{{ provider.name }}</span>
                  <span class="text-2xs font-medium text-dark-28">{{ provider.clinic }}</span>
                </label>
              </div>
              <input
                :id="'select-provider-' + provider.id"
                type="radio"
                name="provider"
                class="mt-5"
                @change="updatePerson('provider', provider)"
              />
            </div>
          </div>
          <div v-if="!loadingProviders && providersLoaded && providers.length == 0">
            {{ t("No providers found") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* globals Craft $ */
import { debounce } from "lodash";
import ToolTip from "./ToolTip.vue";
import FormInput from "./forms/FormInput.vue";
import FormSelect from "./forms/FormSelect.vue";
import FormSwitch from "./forms/FormSwitch.vue";
import CalculatorAdditional from "./CalculatorAdditional.vue";
import SiteLoader from "./SiteLoader.vue";

export default {
  components: {
    ToolTip,
    FormInput,
    FormSelect,
    FormSwitch,
    CalculatorAdditional,
    SiteLoader,
  },
  props: {
    person: {
      type: Object,
      required: true,
    },
    insuranceProviders: {
      type: Object,
      required: true,
    },
    step: {
      type: Number,
      required: true,
    },
    accidentCoverTooltip: {
      type: String,
    },
    doctorTooltip: {
      type: String,
    },
    selectDoctorText: {
      type: String,
    },
    initialPostcode: {
      type: Object,
    },
    errors: {
      type: Array,
    },
  },
  emits: ["update"],
  data() {
    return {
      prices: [],
      deductibles: [],
      postcodes: [],
      optionals: [],
      providers: [],
      genders: {
        male: this.t("Male"),
        female: this.t("Female"),
        prenatal: this.t("Prenatal"),
      },
      priceLoaded: false,
      postcodeLoaded: false,
      optionalsLoaded: false,
      providersLoaded: false,
      loadingPrices: false,
      loadingDeductibles: false,
      loadingPostcodes: false,
      loadingOptionals: false,
      loadingProviders: false,
      showProviders: true,
      debouncePostcodes: debounce((event) => {
        this.loadPostcodes(event.target.value);
      }, 500),
      debounceProviders: debounce((event) => {
        this.loadProviders(event.target.value);
      }, 500),
      datepickerOptions: {
        altInput: true,
        altFormat: "d.m.Y",
        minDate: "1900/01/01",
        maxDate: new Date().toLocaleDateString("en"),
        disableMobile: true,
      },
    };
  },
  computed: {
    loading() {
      return this.loadingPrices || this.loadingDeductibles || this.loadingPostcodes || this.loadingOptionals;
    },
  },
  watch: {
    person: {
      handler(newVal, oldVal) {
        this.onPersonChange(newVal, oldVal);
      },
      deep: true,
    },
    deductibles() {
      this.$nextTick(() => {
        $(this.$refs.deductibleInput).multipleSelect("refresh");
      });
    },
    postcodes() {
      this.$nextTick(() => {
        $(this.$refs.postcodeInput).multipleSelect("refresh");
      });
    },
  },
  created() {
    let date = new Date().toLocaleDateString("en-uk").split("/");
    this.datepickerOptions.maxDate = `${date[2]}/${date[1]}/${date[0]}`;
    this.onPersonChange(this.person, {});
  },
  methods: {
    searchProviders(event) {
      return this.debounceProviders(event);
    },
    onPersonChange(newVal, oldVal) {
      if (newVal.dob !== oldVal.dob) {
        this.loadDeductibles();
      }
      if (newVal.postcode?.value !== oldVal.postcode?.value || newVal.gender !== oldVal.gender) {
        this.loadOptionals();
      }
      if (
        newVal.postcode?.value !== oldVal.postcode?.value ||
        newVal.dob !== oldVal.dob ||
        newVal.deductible?.value !== oldVal.deductible?.value
      ) {
        this.loadPrices();
      }
    },
    updatePerson(field, value) {
      this.$emit("update", {
        [field]: value,
      });
    },
    searchPostcode(event) {
      return this.debouncePostcodes(event);
    },
    loadDeductibles() {
      if (!this.person.dob) {
        return;
      }
      this.loadingDeductibles = true;
      $.ajax({
        url: Craft.currentSite.baseUrl,
        method: "post",
        data: {
          action: "swissmed/calculator/deductibles",
          countryCode: Craft.currentSite.countryCode,
          dob: this.person.dob,
        },
      }).done((data) => {
        let options = data.map((d) => d.value);
        if (!this.person.deductible?.value || !options.includes(parseInt(this.person.deductible.value))) {
          this.updatePerson("deductible", data[0] ?? null);
        }
        this.deductibles = data;
        this.loadingDeductibles = false;
      });
    },
    loadPrices() {
      if (!this.person.postcode?.value || !this.person.deductible?.value || !this.person.dob) {
        return;
      }
      this.loadingPrices = true;
      $.ajax({
        url: Craft.currentSite.baseUrl,
        method: "post",
        data: {
          action: "swissmed/calculator/prices",
          country: Craft.currentSite.countryCode,
          postcode: this.person.postcode.value,
          deductible: this.person.deductible?.value,
          dob: this.person.dob,
        },
      }).then((data) => {
        this.prices = data;
        this.priceLoaded = true;
        this.updatePerson("price", null);
        this.loadingPrices = false;
      });
    },
    loadPostcodes(term) {
      if (term.length < 3) {
        return;
      }
      this.loadingPostcodes = true;
      $.ajax({
        url: Craft.currentSite.baseUrl,
        method: "post",
        data: {
          action: "swissmed/calculator/postcodes",
          countryCode: Craft.currentSite.countryCode,
          term: term,
        },
      }).done((data) => {
        this.postcodes = data;
        this.postcodeLoaded = true;
        this.loadingPostcodes = false;
      });
    },
    loadProviders(term) {
      if (term.length < 3) {
        return;
      }
      this.loadingProviders = true;
      $.ajax({
        url: Craft.currentSite.baseUrl,
        method: "post",
        data: {
          action: "swissmed/calculator/providers",
          term: term,
        },
      }).done((data) => {
        this.providers = data;
        this.providersLoaded = true;
        this.loadingProviders = false;
      });
    },
    loadOptionals() {
      if (!this.person.postcode?.value || !this.person.gender) {
        return;
      }
      this.loadingOptionals = true;
      $.ajax({
        url: Craft.currentSite.baseUrl,
        method: "post",
        data: {
          action: "swissmed/calculator/additionals",
          optionals: 1,
          country: Craft.currentSite.countryCode,
          postcode: this.person.postcode?.value,
          gender: this.person.gender,
        },
      }).done((data) => {
        this.optionals = data;
        this.optionalsLoaded = true;
        this.updatePerson("optionals", []);
        this.loadingOptionals = false;
      });
    },
    toggleOptional(additional, enabled) {
      let adds = [];
      if (enabled) {
        for (let add of this.person.optionals) {
          if (add.group_id != additional.group_id) {
            adds.push(add);
          }
        }
        adds.push(additional);
        this.updatePerson("optionals", adds);
      } else {
        this.updatePerson(
          "optionals",
          this.person.optionals.filter((a) => a.id != additional.id),
        );
      }
    },
  },
};
</script>
